/* eslint-disable no-param-reassign */
import { DroppointListsDetail, PickupPointInfo } from '../droppoint-lists/models/droppoint-lists.models';
import { PhysicalStoreDetail } from '../physical-store/models/physical-store.models';

import { ApiDeliveryPointList, ApiDroppointLists, ApiDroppointListsDetail, ApiPickupPointInfo } from '@api/_api/api-store-locator/api-droppoint-lists/models/api-droppoint-lists.models';
import { ApiPhysicalStore, ApiPhysicalStoreByCountry, ApiPhysicalStoreDetail } from '@api/_api/api-store-locator/api-physical-store/models/api-physical-store.models';

const NUMBER_REGEX = /^\d*$/;

/**
 * Transformer para interactuar con las tiendas del selector de tiendas
 */

// biome-ignore lint/complexity/noStaticOnlyClass: <explanation>
export class PhysicalStoreModelsTransformer {
    /**
     * Tranforma los datos de las tiendas obtenidas de la API
     * @param apiPhysicalStore Listado de tiendas obtenidas desde la API
     * @returns Listado de tiendas bajo el modelo "PhysicalStoreDetail"
     */
    public static fromApi(apiPhysicalStore: ApiPhysicalStore): PhysicalStoreDetail[] {
        // Transformamos el detalle de la tienda y retornamos el array transformado
        const closerStores: PhysicalStoreDetail[] = [];

        // biome-ignore lint/complexity/noForEach: <explanation>
        apiPhysicalStore.closerStores.forEach((store: ApiPhysicalStoreDetail) => {
            // biome-ignore lint/complexity/noThisInStatic: <explanation>
            closerStores.push(this.transformStore(store));
        });

        const stores: PhysicalStoreDetail[] = closerStores;

        return stores;
    }

    /**
     * Tranforma los datos de las tiendas obtenidas de la API
     * @param apiPhysicalStore Listado de tiendas obtenidas desde la API
     * @returns Listado de tiendas bajo el modelo "PhysicalStoreDetail"
     */
    public static fromApiCountry(apiPhysicalStore: ApiPhysicalStoreByCountry): PhysicalStoreDetail[] {
        // Transformamos el detalle de la tienda y retornamos el array transformado
        const closerStores: PhysicalStoreDetail[] = [];

        // biome-ignore lint/complexity/noForEach: <explanation>
        apiPhysicalStore.stores?.forEach((store: ApiPhysicalStoreDetail) => {
            // biome-ignore lint/complexity/noThisInStatic: <explanation>
            closerStores.push(this.transformStore(store));
        });

        const stores: PhysicalStoreDetail[] = closerStores;

        return stores;
    }

    /**
     * Transforma el detalle de la tienda devuelto por la API
     * @param closerStore Detalle de una tienda
     * @returns Detalle de tienda bajo el modelo "PhysicalStoreDetail"
     */
    public static nameContainNumber(name: string, addressLines: string) {
        const noNumberName = name.split(',');
        if (name !== addressLines) {
            return name;
        }

        if (!NUMBER_REGEX.test(noNumberName[0])) {
            return noNumberName[0];
        }
        return noNumberName[1];
    }

    public static transformStore(closerStore: ApiPhysicalStoreDetail): PhysicalStoreDetail {
        const store: PhysicalStoreDetail = {
            id: closerStore.id,
            // biome-ignore lint/complexity/noThisInStatic: <explanation>
            name: this.nameContainNumber(closerStore.name, closerStore.addressLines[0]),
            zipCode: closerStore.zipCode,
            city: closerStore.city,
            sections: closerStore.sections,
            sizeStocks: closerStore.sizeStocks,
            street: closerStore.street,
            latitude: closerStore.latitude,
            longitude: closerStore.longitude,
            address: closerStore.addressLines[0],
            phone: closerStore.phones[0],
            isStorePersonalTailoring: closerStore.isStorePersonalTailoring,
            openingHours: closerStore.openingHours,
            additionalServices: closerStore.additionalServices,
            isBlocked: closerStore.isBlocked,
            isPickupAllowed: closerStore.isPickupAllowed,
            // biome-ignore lint/complexity/useSimplifiedLogicExpression: <explanation>
            isHighVolumeShop: !closerStore.isPickupAllowed || closerStore.isBlocked || false,
            shippingOnlyForEmployees: closerStore.shippingOnlyForEmployees,
            countryCode: closerStore.countryCode,
            state: closerStore.state,
        };

        return store;
    }

    /**
     * Tranforma los datos de los puntos de recogida de la API
     * @param apiDroppointLists Listado de puntos de recogida desde la API
     * @returns Listado de puntos de recojida bajo el modelo "DroppointListsDetail"
     */
    public static droppointsFromApi(apiDroppointLists: ApiDroppointLists): DroppointListsDetail[] {
        // Transformamos el detalle del punto de entrega y retornamos el array transformado
        const dropPoints = [];

        for (const provider of apiDroppointLists.providers) {
            for (const droppoint of apiDroppointLists.dropPoints[provider].droppointInfo) {
                // biome-ignore lint/complexity/noThisInStatic: <explanation>
                dropPoints.push(this.transformDroppoint(droppoint, provider));
            }
        }

        const droppoints: DroppointListsDetail[] = dropPoints;

        return droppoints;
    }

    public static deliveryPointsFromApi(apiDeliveryPointsList: ApiDeliveryPointList): PickupPointInfo[] {
        const deliveryPoints: PickupPointInfo[] = apiDeliveryPointsList.closerPickupPoints
            .map((pickupPointInfo) => PhysicalStoreModelsTransformer.transformDeliveryPoint(pickupPointInfo));

        return deliveryPoints;
    }

    /**
     * Transforma el detalle del punto de recogida devuelto por la API
     * @param closerStore Detalle de un punto de recogida
     * @returns Detalle de un punto de recogida bajo el modelo "StoreLocatorDroppoiDetail"
     */
    private static transformDroppoint(pickupPoint: ApiDroppointListsDetail, provider) {
        // TODO ECOMDUTI-20990 Aqui se debe ponert todo lo que hay en el método processDroppointSearch de store-locator.component.js
        const droppoint = {
            id: pickupPoint.id,
            name: pickupPoint.name,
            zipCode: pickupPoint.zipCode,
            city: pickupPoint.city,
            // biome-ignore lint/suspicious/noAssignInExpressions: <explanation>
            street: pickupPoint.street += ` ${pickupPoint.homeNo}`,
            latitude: pickupPoint.latitude,
            longitude: pickupPoint.longitude,
            openingHours: pickupPoint.openingHours,
            provider,
            stateCode: pickupPoint.stateCode,
            internalId: pickupPoint.internalId,
            district: pickupPoint?.district,
            extraParams: pickupPoint.extraParams ? transformExtraParams(pickupPoint.extraParams) : null,
        };

        return droppoint;
    }

    private static transformDeliveryPoint(pickupPoint: ApiPickupPointInfo): PickupPointInfo {
        return {
            ...pickupPoint,
            zipCode: pickupPoint.address.zipCode,
            city: pickupPoint.address.city,
            sizeStocks: [],
            openingHours: pickupPoint.openingHours?.schedule,
            address: pickupPoint.address.addressLines?.[0],
            street: pickupPoint.address.addressLines?.[0],
            latitude: Number(pickupPoint.address.latitude),
            longitude: Number(pickupPoint.address.longitude),
            // #region PHYSICAL STORE PROPERTIES
            phone: pickupPoint.address.phones?.[0] as string,
            // isStorePersonalTailoring: X,
            additionalServices: pickupPoint.address.additionalServices,
            isPickupAllowed: pickupPoint.isPickupAllowed,
            isHighVolumeShop: !pickupPoint.isPickupAllowed || pickupPoint.isBlocked,
            shippingOnlyForEmployees: pickupPoint.shippingOnlyForEmployees,
            countryCode: pickupPoint.address.countryCode,
            state: pickupPoint.address.state,
            storeDroppointsPromotion: pickupPoint.storeDroppointsPromotion,
            stateCode: pickupPoint.address.stateCode,
            extraParams: pickupPoint.extraParams ? transformExtraParams(pickupPoint.extraParams) : null,
            // #endregion PHYSICAL STORE PROPERTIES
        };
    }
}

export function transformExtraParams(extraParams: Record<string, string>) {
    const { DropPointCustomerId: regex, ...otherProps } = extraParams;
    return { regex, ...otherProps };
}
